// Translations for TeamPower Check App
export const translations = {
  en: {
    // Landing Page
    landing: {
      title: "Measure Your Team's",
      titleAccent: "Pulse",
      subtitle: "Discover your team's strengths across Capability, Collaboration, and Energy. A quick 15-question assessment to unlock your team's potential.",
      enterCode: "Enter Event Code",
      start: "Start",
      admin: "Admin",
      poweredBy: "Powered by Bitou & Summit | Members of International Team Experience Network",
    },
    // Dimensions
    dimensions: {
      capability: "Capability",
      capabilityUpper: "Capability",
      capabilityDesc: "Skills, expertise, processes, and quality standards that drive team performance.",
      collaboration: "Collaboration",
      collaborationUpper: "Collaboration",
      collaborationDesc: "Communication, trust, conflict resolution, and mutual support within the team.",
      energy: "Energy",
      energyUpper: "Energy",
      energyDesc: "Motivation, positivity, resilience, and willingness to innovate and grow.",
      threeDimensions: "Three Dimensions of",
      teamPower: "Team Power",
    },
    // Questionnaire
    questionnaire: {
      stronglyDisagree: "Strongly Disagree",
      stronglyAgree: "Strongly Agree",
      back: "Back",
      next: "Next",
      submit: "Submit",
      submitting: "Submitting...",
    },
    // Questions
    questions: [
      "Our team has the necessary expertise and skills to successfully complete our tasks.",
      "The members of our team bring different competencies that complement each other.",
      "Our team regularly invests in the ongoing development of its members' skills.",
      "We have clear processes and workflows that are implemented efficiently.",
      "The quality of our work results meets the high standards we set for ourselves.",
      "Our team communicates openly, honestly, and constructively with each other.",
      "We support each other and help when someone needs assistance.",
      "Conflicts are seen as opportunities for improvement in our team and are resolved constructively.",
      "There is a high level of trust among the members of our team.",
      "We work toward common goals and don't just pursue individual interests.",
      "Each member feels like a valued part of the team and is appreciated.",
      "Our team is motivated and engaged in pursuing our goals.",
      "We radiate positivity and optimism and transfer this to others.",
      "Our team has the strength and endurance to overcome challenges.",
      "We are willing to try innovative solutions and continue to develop.",
    ],
    // Results
    results: {
      yourIndex: "Your TeamPower Index",
      indexDescription: "This value shows your personal assessment of team power. Your team leader can view the overall evaluation of all team members.",
      averageScore: "Average Score",
      score: "Score",
      recommendation: "Recommendation for Your Team",
      recommendedLevel: "Recommended Level:",
      targetedImpulses: "Your team needs targeted impulses to optimize collaboration.",
      thankYou: "Thank you for completing the TeamPower Check!",
      backToHome: "Back to Home",
      newAssessment: "New Assessment",
      downloadResults: "Download My Results",
      downloadingPdf: "Generating PDF...",
      developmentFocus: "Development Focus",
      teambuildingOutcomes: "Teambuilding Outcomes",
      trainingFeatures: "Training Features",
      priorityFocus: "Priority Focus",
      resetAlertTitle: "Reset Intervention Required",
      resetAlertTitleUpper: "Reset Intervention Required",
      resetAlertDesc: "Your team shows low scores in multiple dimensions. This indicates systemic performance barriers and requires a structured team development journey focusing on shared strategy, role clarity and trust building.",
      interventionIntensity: "Intervention Intensity",
    },
    // PDF specific labels
    pdf: {
      dimension: "Dimension",
      score: "Score",
      level: "Level",
    },
    // Intervention Intensity Bands (Executive tone)
    intervention: {
      foundationClarityJourney: "Foundation & Clarity Journey",
      foundationClarityJourneyDesc: "Your team requires foundational work to establish shared understanding, role clarity and basic performance alignment. A structured development journey is recommended.",
      alignmentConsistencyJourney: "Alignment & Consistency Journey",
      alignmentConsistencyJourneyDesc: "Your team demonstrates potential but requires targeted alignment and capability strengthening to improve consistency and effectiveness.",
      accelerationExecutionJourney: "Acceleration & Execution Journey",
      accelerationExecutionJourneyDesc: "Your team operates effectively but has clear opportunities to improve speed, collaboration quality and strategic alignment.",
      growthLeadershipJourney: "Growth & Leadership Journey",
      growthLeadershipJourneyDesc: "Your team demonstrates strong foundations and is ready to focus on advanced collaboration, innovation and leadership development.",
      strategicImpactInnovationJourney: "Strategic Impact & Innovation Journey",
      strategicImpactInnovationJourneyDesc: "Your team is performing at a very high level. Development focus should shift towards innovation, talent retention and sustaining long-term performance energy.",
    },
    // Dimension Levels (new bands)
    dimensionLevels: {
      criticalCapabilityGap: "Critical Capability Gap",
      developingCapability: "Developing Capability",
      functionalCapability: "Functional Capability",
      advancedCapability: "Advanced Capability",
      collaborationBreakdown: "Collaboration Breakdown",
      misalignmentSilos: "Misalignment / Silos",
      functionalCollaboration: "Functional Collaboration",
      highTrustTeam: "High Trust Team",
      disengagementBurnoutRisk: "Disengagement / Burnout Risk",
      lowMomentum: "Low Momentum",
      stableEnergy: "Stable Energy",
      highDriveTeam: "High Drive Team",
    },
    // Teambuilding Outcomes
    outcomes: {
      skillsFutureReadiness: "Skills & Future Readiness",
      strategyClarityExecution: "Strategy Clarity & Execution",
      accountabilityOwnership: "Accountability & Ownership",
      crossFunctionalCollaboration: "Cross-functional Collaboration",
      leadershipCapability: "Leadership Capability",
      meetingEfficiencyTimeUse: "Meeting Efficiency & Time Use",
      talentRetentionGrowth: "Talent Retention & Growth",
      communicationTransparency: "Communication & Transparency",
      engagementMotivation: "Engagement & Motivation",
      changeReadinessResilience: "Change Readiness & Resilience",
    },
    // Training Features
    training: {
      strategyExecution: "Strategy & Execution",
      personalEffectivenessProductivity: "Personal Effectiveness & Productivity",
      leadershipPeopleManagement: "Leadership & People Management",
      collaborationTeamDynamics: "Collaboration & Team Dynamics",
      innovationFutureSkills: "Innovation & Future Skills",
      changeResilience: "Change & Resilience",
    },
    // Levels
    levels: {
      topLevel: "Top Level",
      topLevelUpper: "Top Level",
      goodLevel: "Good Level",
      goodLevelUpper: "Good Level",
      baseLevel: "Base Level",
      baseLevelUpper: "Base Level",
      developmentNeeded: "Development Needed",
      developmentNeededUpper: "Development Needed",
    },
    // Recommendations
    recommendations: {
      excellent: {
        title: "Excellent – High-performing team",
        description: "Your team demonstrates strong collaboration, skills, and energy. Focus on maintaining this excellence.",
        level: "Level 4-5: Advanced Team Optimization",
      },
      good: {
        title: "Good – Solid foundation with potential",
        description: "Your team works well together, but there is still room for optimization.",
        level: "Level 2-3: Intelligent Team Building or Team Development",
      },
      base: {
        title: "Base – Functional team with growth areas",
        description: "Your team has basic functionality but needs targeted improvements.",
        level: "Level 1-2: Team Building Basics",
      },
      development: {
        title: "Development Needed – Focus on fundamentals",
        description: "Your team needs significant development in core areas.",
        level: "Level 1: Foundation Building",
      },
    },
    // Admin
    admin: {
      adminPanel: "Admin Panel",
      login: "Login",
      logout: "Logout",
      username: "Username",
      password: "Password",
      createAccount: "Create Account",
      backToHome: "Back to Home",
      events: "Events",
      reports: "Reports",
      manageEvents: "Manage your teambuilding events",
      createEvent: "Create Event",
      eventName: "Event Name",
      date: "Date",
      location: "Location",
      country: "Country",
      description: "Description",
      active: "Active",
      inactive: "Inactive",
      view: "View",
      responses: "responses",
      noEvents: "No events yet",
      createFirst: "Create your first event to get started",
      welcomeBack: "Welcome back!",
      // Event Detail
      exportExcel: "Excel",
      exportPdf: "PDF",
      teamPowerIndex: "TeamPower Index",
      individualResponses: "Individual Responses",
      noResponses: "No responses yet",
      shareCode: "Share the event code with participants",
      submitted: "Submitted",
      // Reports
      yearlyBenchmarks: "Yearly and country benchmarks",
      overallTeamPower: "Overall TeamPower",
      totalEvents: "Total Events",
      totalResponses: "Total Responses",
      yearlyTrends: "Yearly Trends",
      yearlyBreakdown: "Yearly Breakdown",
      countryComparison: "Country Comparison (European Benchmark)",
      year: "Year",
      allCountries: "All Countries",
      noData: "No data available",
      // Team Comparison
      teamComparison: "Team Comparison",
      compareEvents: "Compare Events",
      selectEvents: "Select events to compare",
      comparison: "Comparison",
    },
  },
  el: {
    // Landing Page - title/titleAccent use font-heading (uppercase CSS), so no accents
    // But buttons are normal case, so they need accents
    landing: {
      title: "Μετρησε τον Παλμο της",
      titleAccent: "Ομαδας σου",
      subtitle: "Ανακάλυψε τα δυνατά σημεία της ομάδας σου σε Ικανότητα, Συνεργασία και Ενέργεια. Μια γρήγορη αξιολόγηση 15 ερωτήσεων για να ξεκλειδώσεις τις δυνατότητες της ομάδας σου.",
      enterCode: "Εισάγετε Κωδικό Εκδήλωσης",
      start: "Έναρξη",
      admin: "Διαχειριστής",
      poweredBy: "Powered by Bitou & Summit | Members of International Team Experience Network",
    },
    // Dimensions - labels on intro page use text-sm (not uppercase), descriptions are body text
    // For uppercase display, use toUpperCase() on the key without accents
    dimensions: {
      capability: "Ικανότητα",
      capabilityUpper: "Ικανοτητα",
      capabilityDesc: "Δεξιότητες, τεχνογνωσία, διαδικασίες και πρότυπα ποιότητας που οδηγούν την απόδοση της ομάδας.",
      collaboration: "Συνεργασία",
      collaborationUpper: "Συνεργασια",
      collaborationDesc: "Επικοινωνία, εμπιστοσύνη, επίλυση συγκρούσεων και αμοιβαία υποστήριξη εντός της ομάδας.",
      energy: "Ενέργεια",
      energyUpper: "Ενεργεια",
      energyDesc: "Κίνητρα, θετικότητα, ανθεκτικότητα και προθυμία για καινοτομία και ανάπτυξη.",
      threeDimensions: "Τρεις Διαστασεις της",
      teamPower: "Δυναμης Ομαδας",
    },
    // Questionnaire - navigation buttons are NOT uppercase, so need accents
    questionnaire: {
      stronglyDisagree: "Διαφωνώ Απόλυτα",
      stronglyAgree: "Συμφωνώ Απόλυτα",
      back: "Πίσω",
      next: "Επόμενο",
      submit: "Υποβολή",
      submitting: "Υποβολή...",
    },
    // Questions - now displayed in lowercase (normal-case), so need accents
    questions: [
      "Η ομάδα μας διαθέτει την απαραίτητη τεχνογνωσία και δεξιότητες για να ολοκληρώσει επιτυχώς τις εργασίες μας.",
      "Τα μέλη της ομάδας μας φέρνουν διαφορετικές ικανότητες που αλληλοσυμπληρώνονται.",
      "Η ομάδα μας επενδύει τακτικά στη συνεχή ανάπτυξη των δεξιοτήτων των μελών της.",
      "Έχουμε σαφείς διαδικασίες και ροές εργασίας που εφαρμόζονται αποτελεσματικά.",
      "Η ποιότητα των αποτελεσμάτων της εργασίας μας πληροί τα υψηλά πρότυπα που θέτουμε.",
      "Η ομάδα μας επικοινωνεί ανοιχτά, ειλικρινά και εποικοδομητικά μεταξύ της.",
      "Υποστηρίζουμε ο ένας τον άλλον και βοηθάμε όταν κάποιος χρειάζεται βοήθεια.",
      "Οι συγκρούσεις θεωρούνται ευκαιρίες για βελτίωση στην ομάδα μας και επιλύονται εποικοδομητικά.",
      "Υπάρχει υψηλό επίπεδο εμπιστοσύνης μεταξύ των μελών της ομάδας μας.",
      "Εργαζόμαστε προς κοινούς στόχους και δεν επιδιώκουμε μόνο ατομικά συμφέροντα.",
      "Κάθε μέλος αισθάνεται ως πολύτιμο μέρος της ομάδας και εκτιμάται.",
      "Η ομάδα μας είναι παρακινημένη και αφοσιωμένη στην επίτευξη των στόχων μας.",
      "Εκπέμπουμε θετικότητα και αισιοδοξία και τη μεταφέρουμε στους άλλους.",
      "Η ομάδα μας έχει τη δύναμη και την αντοχή να ξεπεράσει τις προκλήσεις.",
      "Είμαστε πρόθυμοι να δοκιμάσουμε καινοτόμες λύσεις και να συνεχίσουμε να αναπτυσσόμαστε.",
    ],
    // Results - body text needs accents, font-heading texts don't
    results: {
      yourIndex: "Ο Δείκτης TeamPower σου",
      indexDescription: "Αυτή η τιμή δείχνει την προσωπική σου αξιολόγηση της δύναμης της ομάδας. Ο αρχηγός της ομάδας μπορεί να δει τη συνολική αξιολόγηση όλων των μελών.",
      averageScore: "Μέση Βαθμολογία",
      score: "Βαθμολογία",
      recommendation: "Συσταση για την Ομαδα σου",
      recommendedLevel: "Προτεινόμενο Επίπεδο:",
      targetedImpulses: "Η ομάδα σου χρειάζεται στοχευμένες παρεμβάσεις για να βελτιστοποιήσει τη συνεργασία.",
      thankYou: "Ευχαριστούμε που ολοκλήρωσες το TeamPower Check!",
      backToHome: "Επιστροφή στην Αρχική",
      newAssessment: "Νέα Αξιολόγηση",
      downloadResults: "Λήψη Αποτελεσμάτων",
      downloadingPdf: "Δημιουργία PDF...",
      developmentFocus: "Εστιαση Αναπτυξης",
      teambuildingOutcomes: "Αποτελέσματα Teambuilding",
      trainingFeatures: "Χαρακτηριστικά Εκπαίδευσης",
      priorityFocus: "Προτεραιότητα",
      resetAlertTitle: "Απαιτείται Επαναφορά",
      resetAlertTitleUpper: "Απαιτειται Επαναφορα",
      resetAlertDesc: "Η ομάδα σου παρουσιάζει χαμηλές βαθμολογίες σε πολλές διαστάσεις. Αυτό υποδεικνύει συστημικά εμπόδια απόδοσης και απαιτεί ένα δομημένο ταξίδι ανάπτυξης ομάδας που εστιάζει στην κοινή στρατηγική, τη σαφήνεια ρόλων και την οικοδόμηση εμπιστοσύνης.",
      interventionIntensity: "Ένταση Παρέμβασης",
    },
    pdf: {
      dimension: "Διάσταση",
      score: "Βαθμολογία",
      level: "Επίπεδο",
    },
    // Intervention Intensity Bands (Executive tone)
    intervention: {
      foundationClarityJourney: "Ταξίδι Θεμελίων & Σαφήνειας",
      foundationClarityJourneyDesc: "Η ομάδα σου χρειάζεται θεμελιώδη εργασία για την εδραίωση κοινής κατανόησης, σαφήνειας ρόλων και βασικής ευθυγράμμισης απόδοσης. Συνιστάται ένα δομημένο ταξίδι ανάπτυξης.",
      alignmentConsistencyJourney: "Ταξίδι Ευθυγράμμισης & Συνέπειας",
      alignmentConsistencyJourneyDesc: "Η ομάδα σου επιδεικνύει δυνατότητες αλλά απαιτεί στοχευμένη ευθυγράμμιση και ενίσχυση ικανοτήτων για βελτίωση της συνέπειας και αποτελεσματικότητας.",
      accelerationExecutionJourney: "Ταξίδι Επιτάχυνσης & Εκτέλεσης",
      accelerationExecutionJourneyDesc: "Η ομάδα σου λειτουργεί αποτελεσματικά αλλά έχει σαφείς ευκαιρίες για βελτίωση ταχύτητας, ποιότητας συνεργασίας και στρατηγικής ευθυγράμμισης.",
      growthLeadershipJourney: "Ταξίδι Ανάπτυξης & Ηγεσίας",
      growthLeadershipJourneyDesc: "Η ομάδα σου επιδεικνύει ισχυρά θεμέλια και είναι έτοιμη να εστιάσει σε προηγμένη συνεργασία, καινοτομία και ανάπτυξη ηγεσίας.",
      strategicImpactInnovationJourney: "Ταξίδι Στρατηγικού Αντίκτυπου & Καινοτομίας",
      strategicImpactInnovationJourneyDesc: "Η ομάδα σου αποδίδει σε πολύ υψηλό επίπεδο. Η εστίαση ανάπτυξης πρέπει να στραφεί προς την καινοτομία, τη διατήρηση ταλέντων και τη διατήρηση μακροχρόνιας ενέργειας απόδοσης.",
    },
    // Dimension Levels (new bands)
    dimensionLevels: {
      criticalCapabilityGap: "Κρίσιμο Κενό Ικανοτήτων",
      developingCapability: "Αναπτυσσόμενη Ικανότητα",
      functionalCapability: "Λειτουργική Ικανότητα",
      advancedCapability: "Προηγμένη Ικανότητα",
      collaborationBreakdown: "Η Συνεργασία χωλαίνει",
      misalignmentSilos: "Αναντιστοιχία / Απομόνωση",
      functionalCollaboration: "Λειτουργική Συνεργασία",
      highTrustTeam: "Ομάδα Υψηλής Εμπιστοσύνης",
      disengagementBurnoutRisk: "Κίνδυνος Αποδέσμευσης / Εξουθένωσης",
      lowMomentum: "Χαμηλή Ορμή",
      stableEnergy: "Σταθερή Ενέργεια",
      highDriveTeam: "Ομάδα Υψηλής Κινητοποίησης",
    },
    // Teambuilding Outcomes
    outcomes: {
      skillsFutureReadiness: "Δεξιότητες & Ετοιμότητα για το Μέλλον",
      strategyClarityExecution: "Σαφήνεια Στρατηγικής & Εκτέλεση",
      accountabilityOwnership: "Λογοδοσία & Ιδιοκτησία",
      crossFunctionalCollaboration: "Διαλειτουργική Συνεργασία",
      leadershipCapability: "Ηγετική Ικανότητα",
      meetingEfficiencyTimeUse: "Αποδοτικότητα Συναντήσεων & Χρήση Χρόνου",
      talentRetentionGrowth: "Διατήρηση Ταλέντων & Ανάπτυξη",
      communicationTransparency: "Επικοινωνία & Διαφάνεια",
      engagementMotivation: "Δέσμευση & Κίνητρα",
      changeReadinessResilience: "Ετοιμότητα Αλλαγής & Ανθεκτικότητα",
    },
    // Training Features
    training: {
      strategyExecution: "Στρατηγική & Εκτέλεση",
      personalEffectivenessProductivity: "Προσωπική Αποτελεσματικότητα & Παραγωγικότητα",
      leadershipPeopleManagement: "Ηγεσία & Διαχείριση Ανθρώπων",
      collaborationTeamDynamics: "Συνεργασία & Δυναμική Ομάδας",
      innovationFutureSkills: "Καινοτομία & Δεξιότητες Μέλλοντος",
      changeResilience: "Αλλαγή & Ανθεκτικότητα",
    },
    // Levels - displayed in badges (normal case), need accents
    // For uppercase display (font-heading), use *Upper versions
    levels: {
      topLevel: "Κορυφαίο Επίπεδο",
      topLevelUpper: "Κορυφαιο Επιπεδο",
      goodLevel: "Καλό Επίπεδο",
      goodLevelUpper: "Καλο Επιπεδο",
      baseLevel: "Βασικό Επίπεδο",
      baseLevelUpper: "Βασικο Επιπεδο",
      developmentNeeded: "Χρειάζεται Ανάπτυξη",
      developmentNeededUpper: "Χρειαζεται Αναπτυξη",
    },
    // Recommendations - titles and descriptions need accents (normal case)
    recommendations: {
      excellent: {
        title: "Εξαιρετικό – Ομάδα υψηλών επιδόσεων",
        description: "Η ομάδα σου επιδεικνύει ισχυρή συνεργασία, δεξιότητες και ενέργεια. Εστίασε στη διατήρηση αυτής της αριστείας.",
        level: "Επίπεδο 4-5: Προηγμένη Βελτιστοποίηση Ομάδας",
      },
      good: {
        title: "Καλό – Στέρεα βάση με δυνατότητες",
        description: "Η ομάδα σου συνεργάζεται καλά, αλλά υπάρχει ακόμα περιθώριο βελτιστοποίησης.",
        level: "Επίπεδο 2-3: Έξυπνη Ομαδοποίηση ή Ανάπτυξη Ομάδας",
      },
      base: {
        title: "Βάση – Λειτουργική ομάδα με περιοχές ανάπτυξης",
        description: "Η ομάδα σου έχει βασική λειτουργικότητα αλλά χρειάζεται στοχευμένες βελτιώσεις.",
        level: "Επίπεδο 1-2: Βασικά Ομαδοποίησης",
      },
      development: {
        title: "Χρειάζεται Ανάπτυξη – Εστίαση στα θεμελιώδη",
        description: "Η ομάδα σου χρειάζεται σημαντική ανάπτυξη σε βασικούς τομείς.",
        level: "Επίπεδο 1: Θεμελίωση",
      },
    },
    // Admin - sidebar uses font-heading (uppercase), form labels are normal case (need accents)
    admin: {
      adminPanel: "Πίνακας Διαχείρισης",
      login: "Σύνδεση",
      logout: "Αποσύνδεση",
      username: "Όνομα χρήστη",
      password: "Κωδικός",
      createAccount: "Δημιουργία Λογαριασμού",
      backToHome: "Επιστροφή στην Αρχική",
      events: "Εκδηλώσεις",
      reports: "Αναφορές",
      manageEvents: "Διαχειριστείτε τις εκδηλώσεις ομαδικότητας",
      createEvent: "Δημιουργία Εκδήλωσης",
      eventName: "Όνομα Εκδήλωσης",
      date: "Ημερομηνία",
      location: "Τοποθεσία",
      country: "Χώρα",
      description: "Περιγραφή",
      active: "Ενεργό",
      inactive: "Ανενεργό",
      view: "Προβολή",
      responses: "απαντήσεις",
      noEvents: "Δεν υπάρχουν εκδηλώσεις",
      createFirst: "Δημιουργήστε την πρώτη σας εκδήλωση για να ξεκινήσετε",
      welcomeBack: "Καλώς ήρθατε πίσω!",
      // Event Detail
      exportExcel: "Excel",
      exportPdf: "PDF",
      teamPowerIndex: "Δείκτης TeamPower",
      individualResponses: "Ατομικές Απαντήσεις",
      noResponses: "Δεν υπάρχουν απαντήσεις ακόμα",
      shareCode: "Μοιραστείτε τον κωδικό εκδήλωσης με τους συμμετέχοντες",
      submitted: "Υποβλήθηκε",
      // Reports
      yearlyBenchmarks: "Ετήσια και εθνικά benchmarks",
      overallTeamPower: "Συνολικό TeamPower",
      totalEvents: "Σύνολο Εκδηλώσεων",
      totalResponses: "Σύνολο Απαντήσεων",
      yearlyTrends: "Ετήσιες Τάσεις",
      yearlyBreakdown: "Ετήσια Ανάλυση",
      countryComparison: "Σύγκριση Χωρών (Ευρωπαϊκό Benchmark)",
      year: "Έτος",
      allCountries: "Όλες οι Χώρες",
      noData: "Δεν υπάρχουν διαθέσιμα δεδομένα",
      // Team Comparison
      teamComparison: "Σύγκριση Ομάδων",
      compareEvents: "Σύγκριση Εκδηλώσεων",
      selectEvents: "Επιλέξτε εκδηλώσεις για σύγκριση",
      comparison: "Σύγκριση",
    },
  },
};

export const getTranslation = (lang, path) => {
  const keys = path.split('.');
  let value = translations[lang] || translations.en;
  for (const key of keys) {
    value = value?.[key];
    if (value === undefined) {
      // Fallback to English
      value = translations.en;
      for (const k of keys) {
        value = value?.[k];
      }
      break;
    }
  }
  return value || path;
};
